import { isApiError } from "@core/api/api-error";
import { ApiService } from "@core/api/api-service";
import { localeToIntlLocale } from "@core/i18n/locale-helper";
import { DEFAULT_LOCALE } from "@domain/localization/locale-manager";
import { NotificationPreferences, User, UserUpdateData } from "@domain/user/user";
import { NonExistentUserError } from "@domain/user/user-errors";
import { dateFromDateDto } from "@utils/date-utils";

interface UserDto
	extends Omit<User, "creationDate" | "lastConnectionDate" | "identityValidationDate" | "hcpValidationDate" | "locale"> {
	creationDate: string;
	lastConnectionDate: string | null;
	hcpValidationDate: string | null;
	identityValidationDate: string | null;
	locale: string | null;
}

interface UserEmailDto {
	email: string;
}

export class UserService {

	private readonly rootPath = "/pts/me";

	constructor(private readonly apiService: ApiService) { }

	async getUser(): Promise<User> {
		try {
			const result = await this.apiService.get<UserDto>(this.rootPath, undefined);
			return UserService.userFromDto(result.data);
		} catch (error) {
			if (isApiError(error) && error.code === "USER_NOT_ACTIVATED") {
				throw new NonExistentUserError("user does not exist");
			} else {
				throw error;
			}
		}
	}

	async getEmailFromUserId(ptId: string): Promise<string | null> {
		try {
			const result = await this.apiService.get<UserEmailDto>(`${this.rootPath}/email`, { params: { ptId } });
			return result.data.email;
		} catch (error) {
			console.error("Failed to fetch email by userId:", error);
			return null;
		}
	}

	async updateNotificationPreferences(notificationPreferences: NotificationPreferences[]): Promise<void> {
		await this.apiService.post(`${this.rootPath}/notification-preferences`, {
			notificationPreferences: notificationPreferences,
		});
	}

	async updateUser(data: UserUpdateData): Promise<User> {
		const result = await this.apiService.post<UserDto>(this.rootPath, data);
		return UserService.userFromDto(result.data);
	}

	async sendNewDeviceNotification(deviceKey: string): Promise<void> {
		await this.apiService.post(`${this.rootPath}/notification/new-device`, { deviceKey });
	}

	async completeIdentityVerification(): Promise<void> {
		await this.apiService.post(`${this.rootPath}/identity-verification/complete`);
	}

	async deleteUser(): Promise<void> {
		await this.apiService.delete(this.rootPath);
	}

	private static userFromDto(userDto: UserDto): User {
		return {
			...userDto,
			lastConnectionDate: userDto.lastConnectionDate ? dateFromDateDto(userDto.lastConnectionDate) : null,
			locale: userDto.locale ? localeToIntlLocale(userDto.locale) : DEFAULT_LOCALE,
			creationDate: dateFromDateDto(userDto.creationDate),
			hcpValidationDate: userDto.hcpValidationDate ? dateFromDateDto(userDto.hcpValidationDate) : null,
			identityValidationDate: userDto.identityValidationDate ? dateFromDateDto(userDto.identityValidationDate) : null,
		};
	}
}
